import path from 'path-browserify'
import { Link, Navigate, Route, Routes, useLocation, useNavigate } from "react-router";
import {OnboardingFillProfile} from './onboarding/fillProfile';
import { useEffect, useState } from 'react';
import formatISO from 'date-fns/formatISO';
import { useClient } from '../providers/supabase';
import { useBackendApi } from '../hooks/useBackendApi';
import { updateMyProfile } from '@healthshare/shared/commands/account';
import uniq from 'lodash/uniq';
import { ProfileRow } from '../../types/supabase';
import { OnboardingSetMembershipDetails } from './onboarding/setMembershipDetails';
import { useUser } from "../hooks/useUser";
import { OnlineRequired } from './onlineRequired';
import { PostgrestError } from '@supabase/supabase-js';
import { SignOutButton } from '../components/signOutButton';
import { Help } from '../routes/help';
import { NotFound } from './notFound';
import AlertBanner from '../components/alertBanner';
import { INVITED_SPOUSE_SKIPPED_STEPS, OnboardingFlowState, OnboardingFlowStates, hasCompletedOnboarding, isInvitedSpouse } from '../reduxToolkit/membershipSlice';
import { useAppSelector } from '../hooks/reduxToolkit';
import { OnboardingHome, OnboardingSubscribeSuccess } from './onboarding/onboardingHome';
import { OnboardingStartTour } from './onboarding/startTour';
import { OnboardingEnableTwoFactor } from './onboarding/enableTwoFactor';
import { OnboardingConsent } from './onboarding/consent';

import './onboarding.scss'

function Onboarding({
  onComplete
} : {
  onComplete: () => void
}) {
  const client = useClient()
  const api = useBackendApi()
  const navigate = useNavigate()
  const location = useLocation()
  const user = useUser()
  const membershipSlice = useAppSelector((s) => s.membership)
  const pathname = path.basename(location.pathname)

  // An invited spouse already had their membership chosen, so drop the "choose
  // membership" step from their flow (they're also skipped past inviting a
  // spouse of their own — see `fillProfile.tsx`). The remaining `flowStates`
  // drives both the step-to-step navigation below and the entry link in
  // `OnboardingHome`.
  const skippedSteps = isInvitedSpouse(membershipSlice) ? INVITED_SPOUSE_SKIPPED_STEPS : []
  const flowStates = OnboardingFlowStates.filter((s) => !skippedSteps.includes(s))
  const currentIdx = flowStates.findIndex((p) => p === pathname)

  const [stepsCompleted, setStepsCompleted] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error>()
  const [wait, setWait] = useState(false)

  useEffect(() => {
    // user.id may be undefined transiently while the auth session is still
    // loading (e.g. on first mount before Supabase resolves the session).
    // Return early and let the effect re-run once user.id is available.
    if (!user?.id) { return }

    client.from('profiles')
      .select('onboarding_steps_completed')
      .eq('id', user.id)
      .then(
        ({ data, error }) => {
          if (error) { setError(new Error(error.message, { cause: error })) }
          if (data && data.length > 0) {
            const profile = data[0] as ProfileRow
            setStepsCompleted(profile.onboarding_steps_completed || [])
          }
          setLoading(false)
        },
        (error) => {
          setError(error)
          setLoading(false)
        }
      )
  }, [client, user?.id])

  if (error) {
    if (/NetworkError/.test(error.message)) {
      return <OnlineRequired onConnectionEstablished={() => {
        setError(undefined)
      }} />
    }
    // else
    throw error
  }

  if (!user) {
    throw new Error(`User is not signed in`)
  }

  if (loading) {
    return <div className="row">
      <div className="col-12 col-lg-6 offset-lg-3">
        <div className={`loading-bar`}></div>
      </div>
    </div>
  }

  const onStepComplete = (step: OnboardingFlowState) => {
    if (!user?.id) { throw new Error('Please sign in first') }

    setWait(true)
    // Record the completed step plus any steps skipped for this user (an invited
    // spouse never visits setMembership). Marking the skipped steps complete keeps
    // `hasCompletedOnboarding` — which checks every step in OnboardingFlowStates —
    // satisfiable for them.
    const newStepsCompleted = uniq([...(stepsCompleted || []), step, ...skippedSteps])
    setStepsCompleted(newStepsCompleted)

    const completeStep = async () => {
      try {
        await updateMyProfile(api, {
          onboarding_step_completed_at: formatISO(new Date()),
          onboarding_steps_completed: newStepsCompleted,
        })
      } catch (error) {
        throw new Error('Failed to complete onboarding step', {
          cause: error,
        });
      }

      if ((currentIdx + 1) >= flowStates.length) {
        console.log('onboarding complete')
        onComplete()
      } else {
        const nextPath = flowStates[currentIdx + 1]
        navigate(nextPath)
      }
    }

    return completeStep()
      .catch((err) => {
        setError(err)
      })
      .finally(() => setWait(false))
  }

  return <div className='App container'>
    <div className="row" style={{ height: '100vh' }}>
      <div className='col-12'>
        <OnboardingTopNav />
        <AlertBanner />
      </div>

      <div className="col-12">
        <Routes>
          <Route index element={<OnboardingHome firstStep={flowStates[0]} hasCompletedOnboarding={hasCompletedOnboarding({ onboarding_steps_completed: stepsCompleted })} />} />
          <Route path="subscribe/success" element={<OnboardingSubscribeSuccess firstStep={flowStates[0]} />} />
          <Route path="setMembership" element={
            skippedSteps.includes('setMembership')
              ? <Navigate to={`/${flowStates[0]}`} replace />
              : <OnboardingSetMembershipDetails user={user} onComplete={() => onStepComplete('setMembership')} />
          } />
          <Route path="fillProfile" element={<OnboardingFillProfile user={user} onProfileFilled={() => onStepComplete('fillProfile')} />} />
          <Route path="consent" element={<OnboardingConsent onComplete={() => onStepComplete('consent')} />} />
          <Route path="enableTwoFactor" element={<OnboardingEnableTwoFactor onComplete={() => onStepComplete('enableTwoFactor')} />} />
          <Route path="startTour" element={<OnboardingStartTour onComplete={() => onStepComplete('startTour')} />} />

          <Route path="/help" element={<Help />} />

          <Route path="*" element={<NotFound />} />
        </Routes>
        <div className={`loading-bar ${wait ? 'visible' : 'invisible'}`}></div>
      </div>
    </div>
  </div>
}

export default Onboarding


function OnboardingTopNav() {
  return <nav className="navbar navbar-expand-lg top-nav">
    <div className="container-fluid d-none d-md-flex">
      <div className='navbar-brand'>
        <h2>HealthShare</h2>
      </div>

      <div className='nav-item'>
        <Link to='/' className='nav-link'>Onboarding</Link>
      </div>


      <div className='nav-item mt-auto'>
        <SignOutButton className='nav-link'>Sign Out</SignOutButton>
      </div>

      <li className='nav-item'>
        <Link to="help" className='nav-link'>
          <i className="material-icons d-none d-md-inline-block">help</i>
          <span className='d-none d-md-inline'>Help</span>
        </Link>
      </li>
    </div>


    <div className="container-fluid d-flex d-md-none">
      <div className='nav-item'>
        <Link to='/' className='nav-link'>
          <i className='material-icons'>home</i>
        </Link>
      </div>

      <div className='nav-item'>
        <Link to="help" className='nav-link'>
          <i className="material-icons">help</i>
        </Link>
      </div>

      <div className='nav-item'>
        <SignOutButton className='nav-link'>
          <i className='material-icons'>logout</i>
        </SignOutButton>
      </div>
    </div>
</nav>
}

